/**
 * RecordHoverCard - Summary hover card for a record
 *
 * Atomic level: Molecule
 * Composes: HoverCard, ConductScoreIndicator, TaxonomyTag
 *
 * Provides a compact, reusable preview of record details with a link to open.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../../../lib/utils';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '../../../ui/hover-card';
import { Button } from '../../../ui/button';
import { Badge } from '../../../ui/badge';
import { ConductScoreIndicator } from './ConductScoreIndicator';
import { ProcessingStatusBadge } from './ProcessingStatusBadge';
import { TaxonomyTag } from './TaxonomyTag';
import type { Record } from '../../../../types/record';

interface RecordHoverCardProps {
  record: Record;
  collectionId?: string;
  recordLink?: string;
  referrerPath?: string;
  children: React.ReactNode;
  className?: string;
  contentClassName?: string;
}

interface RecordHoverCardContentProps {
  record: Record;
  recordLink: string;
  referrerPath?: string;
  className?: string;
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const getSummaryPreview = (record: Record): string | null => {
  if (record.summaryDetailed) {
    return record.summaryDetailed.split('\n\n')[0];
  }
  if (record.verbatim) {
    return record.verbatim;
  }
  return null;
};

const resolveRecordLink = (recordId: string, collectionId?: string, recordLink?: string): string => {
  if (recordLink) return recordLink;
  return `/v4/records/${collectionId || 'default'}/${recordId}`;
};

const RecordHoverCardContent: React.FC<RecordHoverCardContentProps> = ({
  record,
  recordLink,
  referrerPath,
  className,
}) => {
  const summaryPreview = getSummaryPreview(record);
  const rootCauseLabels = record.conductLabels.rootCauses.labels.slice(0, 3);
  const linkState = referrerPath ? { referrer: referrerPath } : undefined;

  return (
    <div className={cn('p-4 space-y-3', className)}>
      <div className="flex items-start gap-3">
        <ConductScoreIndicator score={record.conductScore} size="small" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {(record.processing_status || record._progress?.stage) && (
              <ProcessingStatusBadge record={record} />
            )}
          </div>
          <h3 className="text-sm font-semibold text-foreground leading-snug line-clamp-2">
            {record.summary}
          </h3>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">{record.customerName}</span>
        <span>•</span>
        <span>{formatDate(record.date)}</span>
        <span>•</span>
        <span className="capitalize">{record.operationalContext.source}</span>
      </div>

      {summaryPreview && (
        <div className="border-t border-border pt-3">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            Analysis Summary
          </h4>
          <p className="text-sm text-foreground leading-relaxed line-clamp-4">
            {summaryPreview}
          </p>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {rootCauseLabels.length > 0 ? (
          rootCauseLabels.map((label) => (
            <TaxonomyTag key={label.id} label={label.id} variant="root-cause" />
          ))
        ) : (
          <Badge variant="outline" className="text-xs text-muted-foreground">
            No root causes yet
          </Badge>
        )}
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-border">
        <div className="text-xs text-muted-foreground">
          {record.operationalContext.product}
        </div>
        <Button variant="outline" size="sm" asChild>
          <Link to={recordLink} state={linkState}>Open record</Link>
        </Button>
      </div>
    </div>
  );
};

const RecordHoverCard: React.FC<RecordHoverCardProps> = ({
  record,
  collectionId,
  recordLink,
  referrerPath,
  children,
  className,
  contentClassName,
}) => {
  const resolvedRecordLink = resolveRecordLink(record.id, collectionId, recordLink);

  return (
    <HoverCard openDelay={200} closeDelay={150}>
      <HoverCardTrigger asChild>
        <span className={cn('inline-flex items-center w-full', className)}>
          {children}
        </span>
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        sideOffset={12}
        className={cn('w-[360px] p-0', contentClassName)}
      >
        <RecordHoverCardContent
          record={record}
          recordLink={resolvedRecordLink}
          referrerPath={referrerPath}
        />
      </HoverCardContent>
    </HoverCard>
  );
};

export { RecordHoverCard, RecordHoverCardContent };
